export type CommentNotificationAuthor = {
  id: string;
  name: string;
  email: string;
};

export type AssignmentAlertTierUi = 'soon24h' | 'today' | 'overdue';

export type FileCommentNotification = {
  id: string;
  kind: 'file';
  commentId: string;
  fileId: string;
  fileName: string;
  folderId: string;
  folderName: string;
  containingFolderId: string;
  containingFolderName: string;
  parentId: string | null;
  isReply: boolean;
  author: CommentNotificationAuthor;
  textPreview: string;
  createdAt: string;
  receivedAt: string;
};

export type BoardCardCommentNotification = {
  id: string;
  kind: 'board_card';
  commentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  folderId: string;
  folderName: string;
  parentId: string | null;
  isReply: boolean;
  author: CommentNotificationAuthor;
  textPreview: string;
  createdAt: string;
  receivedAt: string;
};

export type BoardAssignmentDeadlineNotification = {
  id: string;
  kind: 'board_assignment_deadline';
  assignmentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  folderId: string;
  folderName: string;
  tier: AssignmentAlertTierUi;
  deadlineAt: string;
  overdueDays?: number;
  author: CommentNotificationAuthor;
  createdAt: string;
  receivedAt: string;
};

export type BoardAssignmentDigestNotification = {
  id: string;
  kind: 'board_assignment_digest';
  assignmentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  folderId: string;
  folderName: string;
  deadlineAt: string;
  overdueDays: number;
  author: CommentNotificationAuthor;
  createdAt: string;
  receivedAt: string;
};

export type BoardAssignmentAssignedNotification = {
  id: string;
  kind: 'board_assignment_assigned';
  assignmentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  folderId: string;
  folderName: string;
  deadlineAt: string;
  targetColumnKey: string;
  author: CommentNotificationAuthor;
  createdAt: string;
  receivedAt: string;
};

export type CommentNotification =
  | FileCommentNotification
  | BoardCardCommentNotification
  | BoardAssignmentDeadlineNotification
  | BoardAssignmentDigestNotification
  | BoardAssignmentAssignedNotification;

export type CommentNotificationPayload = {
  type: 'comment-notification';
  commentId: string;
  fileId: string;
  fileName: string;
  folderId: string;
  folderName: string;
  containingFolderId: string;
  containingFolderName: string;
  parentId: string | null;
  isReply: boolean;
  author: CommentNotificationAuthor;
  textPreview: string;
  createdAt: string;
  timestamp: string;
};

export type BoardCardCommentNotificationPayload = {
  type: 'board-card-comment-notification';
  commentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  rootFolderId: string;
  folderName: string;
  parentId: string | null;
  isReply: boolean;
  author: CommentNotificationAuthor;
  textPreview: string;
  createdAt: string;
  timestamp: string;
};

export type BoardAssignmentDeadlineNotificationPayload = {
  type: 'board-assignment-deadline-notification';
  assignmentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  rootFolderId: string;
  folderName: string;
  tier: AssignmentAlertTierUi;
  deadlineAt: string;
  overdueDays?: number;
  timestamp: string;
};

export type BoardAssignmentOverdueDigestNotificationPayload = {
  type: 'board-assignment-overdue-digest-notification';
  assignmentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  rootFolderId: string;
  folderName: string;
  deadlineAt: string;
  overdueDays: number;
  assignee: CommentNotificationAuthor;
  timestamp: string;
};

export type BoardAssignmentAssignedNotificationPayload = {
  type: 'board-assignment-assigned-notification';
  assignmentId: string;
  boardId: string;
  rowId: string;
  boardName: string;
  cardTitle: string;
  rootFolderId: string;
  folderName: string;
  deadlineAt: string;
  targetColumnKey: string;
  assigner: CommentNotificationAuthor;
  timestamp: string;
};

export const COMMENT_NOTIFICATIONS_STORAGE_KEY = 'werstack:comment-notifications';

const normalizeStoredNotification = (item: CommentNotification & { kind?: string }): CommentNotification | null => {
  if (item.kind === 'board_assignment_deadline') {
    const deadlineItem = item as BoardAssignmentDeadlineNotification;
    if (!deadlineItem.boardId || !deadlineItem.rowId || !deadlineItem.assignmentId) {
      return null;
    }
    return {
      ...deadlineItem,
      kind: 'board_assignment_deadline',
      boardName: deadlineItem.boardName || '',
      cardTitle: deadlineItem.cardTitle || '',
      folderId: deadlineItem.folderId,
      folderName: deadlineItem.folderName || '',
      author: deadlineItem.author || {
        id: 'system',
        name: deadlineItem.boardName || 'Board',
        email: '',
      },
    };
  }

  if (item.kind === 'board_assignment_digest') {
    const digestItem = item as BoardAssignmentDigestNotification;
    if (!digestItem.boardId || !digestItem.rowId || !digestItem.assignmentId) {
      return null;
    }
    return {
      ...digestItem,
      kind: 'board_assignment_digest',
      boardName: digestItem.boardName || '',
      cardTitle: digestItem.cardTitle || '',
      folderId: digestItem.folderId,
      folderName: digestItem.folderName || '',
      overdueDays: digestItem.overdueDays || 1,
      author: digestItem.author || { id: 'unknown', name: 'Assignee', email: '' },
    };
  }

  if (item.kind === 'board_assignment_assigned') {
    const assignedItem = item as BoardAssignmentAssignedNotification;
    if (!assignedItem.boardId || !assignedItem.rowId || !assignedItem.assignmentId) {
      return null;
    }
    return {
      ...assignedItem,
      kind: 'board_assignment_assigned',
      boardName: assignedItem.boardName || '',
      cardTitle: assignedItem.cardTitle || '',
      folderId: assignedItem.folderId,
      folderName: assignedItem.folderName || '',
      deadlineAt: assignedItem.deadlineAt || '',
      targetColumnKey: assignedItem.targetColumnKey || '',
      author: assignedItem.author || { id: 'unknown', name: 'User', email: '' },
    };
  }

  if (item.kind === 'board_card' || ('boardId' in item && item.boardId && !('assignmentId' in item))) {
    const boardItem = item as BoardCardCommentNotification;
    if (!boardItem.boardId || !boardItem.rowId) {
      return null;
    }
    return {
      ...boardItem,
      kind: 'board_card',
      boardName: boardItem.boardName || '',
      cardTitle: boardItem.cardTitle || '',
      folderId: boardItem.folderId,
      folderName: boardItem.folderName || '',
    };
  }

  const fileItem = item as FileCommentNotification;
  if (!fileItem.fileId) {
    return null;
  }
  return {
    ...fileItem,
    kind: 'file',
    containingFolderId: fileItem.containingFolderId || fileItem.folderId,
    containingFolderName: fileItem.containingFolderName || fileItem.folderName,
  };
};

export const loadStoredCommentNotifications = (userId: string): CommentNotification[] => {
  try {
    const raw = localStorage.getItem(COMMENT_NOTIFICATIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Record<string, CommentNotification[]>;
    return (parsed[userId] || [])
      .map((item) => normalizeStoredNotification(item))
      .filter((item): item is CommentNotification => Boolean(item));
  } catch {
    return [];
  }
};

export const saveStoredCommentNotifications = (
  userId: string,
  notifications: CommentNotification[],
): void => {
  try {
    const raw = localStorage.getItem(COMMENT_NOTIFICATIONS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[userId] = notifications;
    localStorage.setItem(COMMENT_NOTIFICATIONS_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    /* ignore storage errors */
  }
};

export const OPEN_FILE_COMMENT_EVENT = 'werstack:open-file-comment';

export type OpenFileCommentDetail = {
  fileId: string;
  folderId: string;
  folderName: string;
  containingFolderId: string;
  containingFolderName: string;
};

export const PENDING_OPEN_FILE_COMMENT_KEY = 'werstack:pending-open-file-comment';

export const toOpenFileCommentDetail = (
  notification: FileCommentNotification,
): OpenFileCommentDetail => ({
  fileId: notification.fileId,
  folderId: notification.folderId,
  folderName: notification.folderName,
  containingFolderId: notification.containingFolderId || notification.folderId,
  containingFolderName: notification.containingFolderName || notification.folderName,
});

export const storePendingOpenFileComment = (detail: OpenFileCommentDetail): void => {
  try {
    sessionStorage.setItem(PENDING_OPEN_FILE_COMMENT_KEY, JSON.stringify(detail));
  } catch {
    /* ignore */
  }
};

export const peekPendingOpenFileComment = (): OpenFileCommentDetail | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_OPEN_FILE_COMMENT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as OpenFileCommentDetail;
    if (!parsed?.fileId || !parsed?.folderId) {
      return null;
    }
    return {
      fileId: parsed.fileId,
      folderId: parsed.folderId,
      folderName: parsed.folderName || '',
      containingFolderId: parsed.containingFolderId || parsed.folderId,
      containingFolderName: parsed.containingFolderName || parsed.folderName || '',
    };
  } catch {
    return null;
  }
};

export const clearPendingOpenFileComment = (): void => {
  try {
    sessionStorage.removeItem(PENDING_OPEN_FILE_COMMENT_KEY);
  } catch {
    /* ignore */
  }
};

export const consumePendingOpenFileComment = (): OpenFileCommentDetail | null => {
  const pending = peekPendingOpenFileComment();
  if (pending) {
    clearPendingOpenFileComment();
  }
  return pending;
};

export const getCommentNotificationTitle = (notification: CommentNotification): string => {
  if (
    notification.kind === 'board_card'
    || notification.kind === 'board_assignment_deadline'
    || notification.kind === 'board_assignment_digest'
    || notification.kind === 'board_assignment_assigned'
  ) {
    return notification.cardTitle || notification.boardName || 'Board card';
  }
  return notification.fileName;
};

export const getCommentNotificationSubtitle = (notification: CommentNotification): string => {
  if (
    notification.kind === 'board_card'
    || notification.kind === 'board_assignment_deadline'
    || notification.kind === 'board_assignment_digest'
    || notification.kind === 'board_assignment_assigned'
  ) {
    return notification.boardName || notification.folderName;
  }
  return notification.folderName;
};

export const isBoardLinkedNotification = (
  notification: CommentNotification,
): notification is BoardCardCommentNotification
  | BoardAssignmentDeadlineNotification
  | BoardAssignmentDigestNotification
  | BoardAssignmentAssignedNotification => (
  notification.kind === 'board_card'
  || notification.kind === 'board_assignment_deadline'
  || notification.kind === 'board_assignment_digest'
  || notification.kind === 'board_assignment_assigned'
);
